/**
 * Centralized Theme Configuration
 *
 * Supports light and dark modes. Use createAppTheme('light') or createAppTheme('dark')
 * to get a theme object for the desired mode.
 *
 * THEMING GUIDE:
 *
 * 1. Using Color Combinations for Cards:
 *    const cardStyles = theme.getCardStyles('canvas-core', { border: true });
 *    <div style={cardStyles}>Card content</div>
 *
 * 2. Using Color Combinations for Text:
 *    const textStyles = theme.getTextStyles('canvas-core');
 *    <p style={textStyles}>Text content</p>
 *
 * 3. Accessing Individual Colors:
 *    theme.colors.CORE, theme.colors.LUMO, theme.colors.PLUM, etc.
 *
 * 4. In components, use the useAppTheme() hook:
 *    const { theme, mode, toggleTheme } = useAppTheme();
 */

import React from 'react';

type ColorCombination = { background: string; foreground: string };
type ColorCombinations = Record<string, ColorCombination>;
type CardOptions = { border?: boolean; additionalStyles?: React.CSSProperties };
export type ThemeMode = 'light' | 'dark';

// Font family configuration
const fontFamily = {
  primary: '"Bureau Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica", "Arial", sans-serif',
  monospace: 'Monaco, "Cascadia Code", "Roboto Mono", Consolas, "Courier New", monospace',
};

// Primary Palette
const primaryPalette = {
  CORE: '#27236A',
  LUMO: '#7D97EB',
  HALO: '#AEA6F9',
  WAVE: '#060723',
  NIGHT: '#050505',
  SPACE: '#77797C',
  SHELL: '#DEDEDE',
  MIST: '#E4E7EA',
  CANVAS: '#F3F6F6',
  WHITE: '#FFFFFF',
};

// Secondary Palette
const secondaryPalette = {
  PLUM: '#7D6CB1',
  STEM: '#3A5F7C',
  GEO: '#035D57',
  TONE: '#5E5D52',
  DRIFT: '#F4B474',
  SLATE: '#91BBF1',
  ALIGN: '#72CEA4',
  NODE: '#E3F395',
  BLUSH: '#F9F1FC',
  SKY: '#DBF4FF',
  DEW: '#C6EFE2',
  RAY: '#F1F9CD',
};

// ============================================================================
// Light Mode Colors
// ============================================================================

const lightGraphicCombinations: ColorCombinations = {
  'lumo-night': { background: primaryPalette.LUMO, foreground: primaryPalette.NIGHT },
  'space-night': { background: primaryPalette.SPACE, foreground: primaryPalette.NIGHT },
  'shell-night': { background: primaryPalette.SHELL, foreground: primaryPalette.NIGHT },
  'plum-night': { background: secondaryPalette.PLUM, foreground: primaryPalette.NIGHT },
  'drift-night': { background: secondaryPalette.DRIFT, foreground: primaryPalette.NIGHT },
  'slate-wave': { background: secondaryPalette.SLATE, foreground: primaryPalette.WAVE },
  'lumo-wave': { background: primaryPalette.LUMO, foreground: primaryPalette.WAVE },
  'mist-wave': { background: primaryPalette.MIST, foreground: primaryPalette.WAVE },
  'canvas-wave': { background: primaryPalette.CANVAS, foreground: primaryPalette.WAVE },
  'wave-space': { background: primaryPalette.WAVE, foreground: primaryPalette.SPACE },
  'canvas-space': { background: primaryPalette.CANVAS, foreground: primaryPalette.SPACE },
  'wave-shell': { background: primaryPalette.WAVE, foreground: primaryPalette.SHELL },
  'tone-shell': { background: secondaryPalette.TONE, foreground: primaryPalette.SHELL },
  'stem-mist': { background: secondaryPalette.STEM, foreground: primaryPalette.MIST },
  'core-mist': { background: primaryPalette.CORE, foreground: primaryPalette.MIST },
  'space-canvas': { background: primaryPalette.SPACE, foreground: primaryPalette.CANVAS },
  'plum-canvas': { background: secondaryPalette.PLUM, foreground: primaryPalette.CANVAS },
  'canvas-plum': { background: primaryPalette.CANVAS, foreground: secondaryPalette.PLUM },
  'canvas-stem': { background: primaryPalette.CANVAS, foreground: secondaryPalette.STEM },
  'canvas-geo': { background: primaryPalette.CANVAS, foreground: secondaryPalette.GEO },
  'canvas-tone': { background: primaryPalette.CANVAS, foreground: secondaryPalette.TONE },
  'white-stem': { background: primaryPalette.WHITE, foreground: secondaryPalette.STEM },
  'wave-lumo': { background: primaryPalette.WAVE, foreground: primaryPalette.LUMO },
  'wave-slate': { background: primaryPalette.WAVE, foreground: secondaryPalette.SLATE },
  'wave-drift': { background: primaryPalette.WAVE, foreground: secondaryPalette.DRIFT },
  'wave-align': { background: primaryPalette.WAVE, foreground: secondaryPalette.ALIGN },
  'wave-node': { background: primaryPalette.WAVE, foreground: secondaryPalette.NODE },
  'wave-sky': { background: primaryPalette.WAVE, foreground: secondaryPalette.SKY },
};

const lightTextCombinations: ColorCombinations = {
  'shell-core': { background: primaryPalette.SHELL, foreground: primaryPalette.CORE },
  'canvas-core': { background: primaryPalette.CANVAS, foreground: primaryPalette.CORE },
  'space-core': { background: primaryPalette.SPACE, foreground: primaryPalette.CORE },
  'lumo-core': { background: primaryPalette.LUMO, foreground: primaryPalette.CORE },
  'plum-wave': { background: secondaryPalette.PLUM, foreground: primaryPalette.WAVE },
  'drift-wave': { background: secondaryPalette.DRIFT, foreground: primaryPalette.WAVE },
  'align-wave': { background: secondaryPalette.ALIGN, foreground: primaryPalette.WAVE },
  'wave-space': { background: primaryPalette.WAVE, foreground: primaryPalette.SPACE },
  'core-shell': { background: primaryPalette.CORE, foreground: primaryPalette.SHELL },
  'core-mist': { background: primaryPalette.CORE, foreground: primaryPalette.MIST },
  'core-canvas': { background: primaryPalette.CORE, foreground: primaryPalette.CANVAS },
  'core-plum': { background: primaryPalette.CORE, foreground: secondaryPalette.PLUM },
  'canvas-stem': { background: primaryPalette.CANVAS, foreground: secondaryPalette.STEM },
  'canvas-geo': { background: primaryPalette.CANVAS, foreground: secondaryPalette.GEO },
  'canvas-tone': { background: primaryPalette.CANVAS, foreground: secondaryPalette.TONE },
  'drift-core': { background: secondaryPalette.DRIFT, foreground: primaryPalette.CORE },
  'core-slate': { background: primaryPalette.CORE, foreground: secondaryPalette.SLATE },
  'core-align': { background: primaryPalette.CORE, foreground: secondaryPalette.ALIGN },
  'core-node': { background: primaryPalette.CORE, foreground: secondaryPalette.NODE },
  'core-blush': { background: primaryPalette.CORE, foreground: secondaryPalette.BLUSH },
  'core-sky': { background: primaryPalette.CORE, foreground: secondaryPalette.SKY },
  'core-dew': { background: primaryPalette.CORE, foreground: secondaryPalette.DEW },
  'core-ray': { background: primaryPalette.CORE, foreground: secondaryPalette.RAY },
};

const buildLightColors = (graphicCombinations: ColorCombinations, textCombinations: ColorCombinations) => ({
  ...primaryPalette,
  ...secondaryPalette,

  primary: primaryPalette.MIST,
  secondary: primaryPalette.CORE,
  success: secondaryPalette.GEO,
  danger: '#dc3545',
  warning: secondaryPalette.DRIFT,
  info: secondaryPalette.SLATE,
  light: primaryPalette.CANVAS,
  dark: primaryPalette.NIGHT,
  lightGray: primaryPalette.CANVAS,
  mediumGray: primaryPalette.MIST,

  background: {
    main: primaryPalette.WHITE,
    modal: primaryPalette.CANVAS,
    sidebar: primaryPalette.CANVAS,
    iconMenu: primaryPalette.CANVAS,
    button: primaryPalette.MIST,
  },
  background_hover: {
    button: primaryPalette.SHELL,
  },

  button: {
    core: { bg: primaryPalette.CORE, text: primaryPalette.WHITE, hoverBg: primaryPalette.LUMO },
    lumo: { bg: primaryPalette.LUMO, text: primaryPalette.NIGHT, hoverBg: primaryPalette.HALO },
    halo: { bg: primaryPalette.HALO, text: primaryPalette.WAVE, hoverBg: primaryPalette.LUMO },
    plum: { bg: secondaryPalette.PLUM, text: primaryPalette.WHITE, hoverBg: '#9484C7' },
    stem: { bg: secondaryPalette.STEM, text: primaryPalette.WHITE, hoverBg: '#4A7A9C' },
    geo: { bg: secondaryPalette.GEO, text: primaryPalette.WHITE, hoverBg: '#047D73' },
    drift: { bg: secondaryPalette.DRIFT, text: primaryPalette.WAVE, hoverBg: '#F6C594' },
    slate: { bg: secondaryPalette.SLATE, text: primaryPalette.WAVE, hoverBg: '#A7CCF5' },
    align: { bg: secondaryPalette.ALIGN, text: primaryPalette.WAVE, hoverBg: '#8EDAB4' },
    danger: { bg: '#dc3545', text: primaryPalette.WHITE, hoverBg: '#ff5c5c' },
  },

  border: primaryPalette.SHELL,

  text: {
    primary: primaryPalette.CORE,
    secondary: primaryPalette.WAVE,
    muted: primaryPalette.SPACE,
    inverse: primaryPalette.WHITE,
  },

  accent: primaryPalette.WAVE,

  overlay: 'rgba(39, 35, 106, 0.565)',
  overlayDark: 'rgba(0, 0, 0, 0.5)',
  tooltip: primaryPalette.NIGHT,

  status: {
    active: secondaryPalette.ALIGN,
    inactive: secondaryPalette.SKY,
    executing: secondaryPalette.DRIFT,
    completed: secondaryPalette.DEW,
    error: '#fee2e2',
    executingText: '#92400e',
    completedText: secondaryPalette.GEO,
    errorText: '#991b1b',
    success: '#22c55e',
    warning: '#f59e0b',
    info: '#3b82f6',
    successBg: 'rgba(34, 197, 94, 0.1)',
    warningBg: 'rgba(245, 158, 11, 0.1)',
    errorBg: 'rgba(239, 68, 68, 0.1)',
    infoBg: 'rgba(59, 130, 246, 0.1)',
  },

  chart: { border: primaryPalette.SHELL },

  divider: primaryPalette.SPACE,

  error: {
    background: '#fee2e2',
    border: '#dc3545',
    text: '#991b1b',
  },

  toggle: { inactive: primaryPalette.SPACE },

  hover: { iconMenu: primaryPalette.MIST },

  connection: {
    connectedBg: 'rgba(76, 175, 80, 0.15)',
    connectedText: '#4CAF50',
    connectingBg: 'rgba(255, 193, 7, 0.15)',
    connectingText: '#FFC107',
    disconnectedBg: 'rgba(244, 67, 54, 0.15)',
    disconnectedText: '#F44336',
  },

  code: {
    background: 'rgba(0, 0, 0, 0.05)',
    border: 'rgba(0, 0, 0, 0.1)',
  },

  palette: {
    primary: primaryPalette,
    secondary: secondaryPalette,
  },

  combinations: {
    graphic: graphicCombinations,
    text: textCombinations,
  },
});

// ============================================================================
// Dark Mode Colors
// ============================================================================

const darkPalette = {
  surface0: '#0E0E1A',
  surface1: '#151524',
  surface2: '#1C1C30',
  surface3: '#24243D',
  surface4: '#2D2D4A',
  borderDark: '#333355',
  borderLight: '#444466',
  textPrimary: '#E8E8F0',
  textSecondary: '#B0B0C8',
  textMuted: '#7878A0',
};

const darkGraphicCombinations: ColorCombinations = {
  'lumo-night': { background: primaryPalette.LUMO, foreground: primaryPalette.NIGHT },
  'space-night': { background: primaryPalette.SPACE, foreground: primaryPalette.NIGHT },
  'shell-night': { background: darkPalette.surface3, foreground: darkPalette.textPrimary },
  'plum-night': { background: secondaryPalette.PLUM, foreground: primaryPalette.NIGHT },
  'drift-night': { background: secondaryPalette.DRIFT, foreground: primaryPalette.NIGHT },
  'slate-wave': { background: secondaryPalette.SLATE, foreground: primaryPalette.WAVE },
  'lumo-wave': { background: primaryPalette.LUMO, foreground: primaryPalette.WAVE },
  'mist-wave': { background: darkPalette.surface3, foreground: darkPalette.textPrimary },
  'canvas-wave': { background: darkPalette.surface1, foreground: darkPalette.textPrimary },
  'wave-space': { background: darkPalette.surface0, foreground: darkPalette.textSecondary },
  'canvas-space': { background: darkPalette.surface1, foreground: darkPalette.textSecondary },
  'wave-shell': { background: darkPalette.surface0, foreground: darkPalette.textPrimary },
  'tone-shell': { background: secondaryPalette.TONE, foreground: darkPalette.textPrimary },
  'stem-mist': { background: secondaryPalette.STEM, foreground: darkPalette.textPrimary },
  'core-mist': { background: primaryPalette.CORE, foreground: darkPalette.textPrimary },
  'space-canvas': { background: darkPalette.surface2, foreground: darkPalette.textPrimary },
  'plum-canvas': { background: secondaryPalette.PLUM, foreground: darkPalette.textPrimary },
  'canvas-plum': { background: darkPalette.surface1, foreground: secondaryPalette.PLUM },
  'canvas-stem': { background: darkPalette.surface1, foreground: '#5A8FAC' },
  'canvas-geo': { background: darkPalette.surface1, foreground: '#0A8A82' },
  'canvas-tone': { background: darkPalette.surface1, foreground: '#8E8D7E' },
  'white-stem': { background: darkPalette.surface2, foreground: '#5A8FAC' },
  'wave-lumo': { background: darkPalette.surface0, foreground: primaryPalette.LUMO },
  'wave-slate': { background: darkPalette.surface0, foreground: secondaryPalette.SLATE },
  'wave-drift': { background: darkPalette.surface0, foreground: secondaryPalette.DRIFT },
  'wave-align': { background: darkPalette.surface0, foreground: secondaryPalette.ALIGN },
  'wave-node': { background: darkPalette.surface0, foreground: secondaryPalette.NODE },
  'wave-sky': { background: darkPalette.surface0, foreground: secondaryPalette.SKY },
};

const darkTextCombinations: ColorCombinations = {
  'shell-core': { background: darkPalette.surface3, foreground: darkPalette.textPrimary },
  'canvas-core': { background: darkPalette.surface1, foreground: darkPalette.textPrimary },
  'space-core': { background: darkPalette.surface2, foreground: darkPalette.textPrimary },
  'lumo-core': { background: primaryPalette.LUMO, foreground: darkPalette.surface0 },
  'plum-wave': { background: secondaryPalette.PLUM, foreground: darkPalette.textPrimary },
  'drift-wave': { background: secondaryPalette.DRIFT, foreground: darkPalette.surface0 },
  'align-wave': { background: secondaryPalette.ALIGN, foreground: darkPalette.surface0 },
  'wave-space': { background: darkPalette.surface0, foreground: darkPalette.textSecondary },
  'core-shell': { background: primaryPalette.CORE, foreground: darkPalette.textPrimary },
  'core-mist': { background: primaryPalette.CORE, foreground: darkPalette.textPrimary },
  'core-canvas': { background: primaryPalette.CORE, foreground: darkPalette.textPrimary },
  'core-plum': { background: darkPalette.surface1, foreground: '#9D8CD1' },
  'canvas-stem': { background: darkPalette.surface1, foreground: '#5A8FAC' },
  'canvas-geo': { background: darkPalette.surface1, foreground: '#0A8A82' },
  'canvas-tone': { background: darkPalette.surface1, foreground: '#8E8D7E' },
  'drift-core': { background: secondaryPalette.DRIFT, foreground: darkPalette.surface0 },
  'core-slate': { background: darkPalette.surface1, foreground: secondaryPalette.SLATE },
  'core-align': { background: darkPalette.surface1, foreground: secondaryPalette.ALIGN },
  'core-node': { background: darkPalette.surface1, foreground: secondaryPalette.NODE },
  'core-blush': { background: darkPalette.surface1, foreground: secondaryPalette.BLUSH },
  'core-sky': { background: darkPalette.surface1, foreground: secondaryPalette.SKY },
  'core-dew': { background: darkPalette.surface1, foreground: secondaryPalette.DEW },
  'core-ray': { background: darkPalette.surface1, foreground: secondaryPalette.RAY },
};

const buildDarkColors = (graphicCombinations: ColorCombinations, textCombinations: ColorCombinations) => ({
  ...primaryPalette,
  ...secondaryPalette,

  // Override base palette names for dark
  CANVAS: darkPalette.surface1,
  WHITE: darkPalette.surface0,
  SHELL: darkPalette.borderDark,
  MIST: darkPalette.surface3,

  primary: darkPalette.surface3,
  secondary: primaryPalette.LUMO,
  success: '#22c55e',
  danger: '#ef4444',
  warning: secondaryPalette.DRIFT,
  info: secondaryPalette.SLATE,
  light: darkPalette.surface2,
  dark: darkPalette.textPrimary,
  lightGray: darkPalette.surface2,
  mediumGray: darkPalette.surface3,

  background: {
    main: darkPalette.surface0,
    modal: darkPalette.surface1,
    sidebar: darkPalette.surface1,
    iconMenu: darkPalette.surface1,
    button: darkPalette.surface3,
  },
  background_hover: {
    button: darkPalette.surface4,
  },

  button: {
    core: { bg: primaryPalette.CORE, text: darkPalette.textPrimary, hoverBg: primaryPalette.LUMO },
    lumo: { bg: primaryPalette.LUMO, text: darkPalette.surface0, hoverBg: primaryPalette.HALO },
    halo: { bg: primaryPalette.HALO, text: darkPalette.surface0, hoverBg: primaryPalette.LUMO },
    plum: { bg: secondaryPalette.PLUM, text: darkPalette.textPrimary, hoverBg: '#9484C7' },
    stem: { bg: secondaryPalette.STEM, text: darkPalette.textPrimary, hoverBg: '#4A7A9C' },
    geo: { bg: secondaryPalette.GEO, text: darkPalette.textPrimary, hoverBg: '#0A8A82' },
    drift: { bg: secondaryPalette.DRIFT, text: darkPalette.surface0, hoverBg: '#F6C594' },
    slate: { bg: secondaryPalette.SLATE, text: darkPalette.surface0, hoverBg: '#A7CCF5' },
    align: { bg: secondaryPalette.ALIGN, text: darkPalette.surface0, hoverBg: '#8EDAB4' },
    danger: { bg: '#ef4444', text: darkPalette.textPrimary, hoverBg: '#f87171' },
  },

  border: darkPalette.borderDark,

  text: {
    primary: darkPalette.textPrimary,
    secondary: darkPalette.textSecondary,
    muted: darkPalette.textMuted,
    inverse: primaryPalette.NIGHT,
  },

  accent: primaryPalette.LUMO,

  overlay: 'rgba(0, 0, 0, 0.65)',
  overlayDark: 'rgba(0, 0, 0, 0.7)',
  tooltip: darkPalette.surface3,

  status: {
    active: secondaryPalette.GEO,
    inactive: primaryPalette.SPACE,
    executing: secondaryPalette.DRIFT,
    completed: secondaryPalette.DEW,
    error: 'rgba(239, 68, 68, 0.15)',
    executingText: darkPalette.textPrimary,
    completedText: secondaryPalette.ALIGN,
    errorText: '#f87171',
    success: '#22c55e',
    warning: '#f59e0b',
    info: '#60a5fa',
    successBg: 'rgba(34, 197, 94, 0.15)',
    warningBg: 'rgba(245, 158, 11, 0.15)',
    errorBg: 'rgba(239, 68, 68, 0.15)',
    infoBg: 'rgba(96, 165, 250, 0.15)',
  },

  chart: { border: darkPalette.borderDark },

  divider: darkPalette.borderLight,

  error: {
    background: 'rgba(239, 68, 68, 0.15)',
    border: '#ef4444',
    text: '#f87171',
  },

  toggle: { inactive: darkPalette.surface4 },

  hover: { iconMenu: darkPalette.surface3 },

  connection: {
    connectedBg: 'rgba(76, 175, 80, 0.2)',
    connectedText: '#66BB6A',
    connectingBg: 'rgba(255, 193, 7, 0.2)',
    connectingText: '#FFCA28',
    disconnectedBg: 'rgba(244, 67, 54, 0.2)',
    disconnectedText: '#EF5350',
  },

  code: {
    background: 'rgba(255, 255, 255, 0.05)',
    border: 'rgba(255, 255, 255, 0.1)',
  },

  palette: {
    primary: primaryPalette,
    secondary: secondaryPalette,
  },

  combinations: {
    graphic: graphicCombinations,
    text: textCombinations,
  },
});

// ============================================================================
// Shared non-color values
// ============================================================================

const spacing = {
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
};

const borderRadius = {
  sm: '4px',
  md: '8px',
  lg: '20px',
  xl: '30px',
  xxl: '40px',
};

const fontSize = {
  xs: '12px',
  sm: '13px',
  md: '14px',
  lg: '15px',
  xl: '16px',
  xxl: '18px',
  xxxl: '20px',
  title: '24px',
  largeTitle: '28px',
};

const fontWeight = {
  light: '300',
  normal: '400',
  medium: '500',
  semibold: '600',
  bold: '700',
};

const lightShadows = {
  hint: '0 0 3px rgba(0, 0, 0, 0.2)',
  leftLayout: '0 0 4px rgba(0, 0, 0, 0.2)',
  modal: '0 0 7px rgba(0, 0, 0, 0.2)',
  dropdown: '0 3px 3px rgba(0, 0, 0, 0.3)',
};

const darkShadows = {
  hint: '0 0 3px rgba(0, 0, 0, 0.5)',
  leftLayout: '0 0 4px rgba(0, 0, 0, 0.5)',
  modal: '0 0 10px rgba(0, 0, 0, 0.6)',
  dropdown: '0 3px 6px rgba(0, 0, 0, 0.6)',
};

const transitions = {
  fast: 'all 0.2s ease',
  slow: 'all 0.3s ease',
};

const zIndex = {
  tooltip: 1000,
  modal: 2000,
  header: 100,
  sidebar: 200,
};

const dimensions = {
  iconSize: '25px',
  buttonHeight: '60px',
  toggleHeight: '24px',
  toggleWidth: '44px',
  thumbnailSize: '48px',
  maxModalWidth: '1200px',
  maxModalHeight: '80vh',
  maxChartHeight: '200px',
  sidebarWidth: '280px',
  iconMenuWidth: '70px',
  contextPanelMinimizedWidth: '60px',
  contextPanelNormalWidth: '350px',
  headerLogoWidth: '50px',
};

const grid = {
  columns: {
    auto: 'repeat(auto-fit, minmax(200px, 1fr))',
    autoLarge: 'repeat(auto-fit, minmax(300px, 1fr))',
    two: 'repeat(2, 1fr)',
    three: 'repeat(3, 1fr)',
  },
};

const lightScrollbar = {
  width: '8px',
  widthSmall: '6px',
  track: '#f3f6f6',
  trackSmall: '#f3f6f6',
  thumb: '#e4e7ea',
  thumbHover: '#dedede',
};

const darkScrollbar = {
  width: '8px',
  widthSmall: '6px',
  track: darkPalette.surface1,
  trackSmall: darkPalette.surface1,
  thumb: darkPalette.surface4,
  thumbHover: darkPalette.borderLight,
};

// ============================================================================
// Theme Factory
// ============================================================================

const buildTheme = (
  mode: ThemeMode,
  colors: ReturnType<typeof buildLightColors> | ReturnType<typeof buildDarkColors>,
  shadows: typeof lightShadows,
  scrollbar: typeof lightScrollbar,
  graphicCombinations: ColorCombinations,
  textCombinations: ColorCombinations,
) => {
  const getGraphicCombination = (name: string): ColorCombination | null => {
    const key = name.toLowerCase().replace(/\s+/g, '-');
    return graphicCombinations[key] || null;
  };

  const getTextCombination = (name: string): ColorCombination | null => {
    const key = name.toLowerCase().replace(/\s+/g, '-');
    return textCombinations[key] || null;
  };

  const getCardStyles = (combinationName: string, options: CardOptions = {}): React.CSSProperties => {
    const combo = getGraphicCombination(combinationName) || getTextCombination(combinationName);
    if (!combo) {
      console.warn(`Combination "${combinationName}" not found`);
      return {};
    }
    return {
      backgroundColor: combo.background,
      color: combo.foreground,
      border: options.border ? `1px solid ${combo.foreground}` : 'none',
      ...options.additionalStyles,
    };
  };

  const getTextStyles = (combinationName = 'canvas-core', options: CardOptions = {}): React.CSSProperties => {
    const combo = getTextCombination(combinationName) || getGraphicCombination(combinationName);
    if (!combo) {
      console.warn(`Combination "${combinationName}" not found`);
      return {};
    }
    return {
      backgroundColor: combo.background,
      color: combo.foreground,
      ...options.additionalStyles,
    };
  };

  return {
    mode,
    fontFamily,
    colors,
    spacing,
    borderRadius,
    fontSize,
    fontWeight,
    shadows,
    transitions,
    zIndex,
    dimensions,
    grid,
    scrollbar,
    getGraphicCombination,
    getTextCombination,
    getCardStyles,
    getTextStyles,
  };
};

/**
 * Create a theme object for the specified mode.
 */
export const createAppTheme = (mode: ThemeMode = 'light') => {
  if (mode === 'dark') {
    return buildTheme(
      'dark',
      buildDarkColors(darkGraphicCombinations, darkTextCombinations),
      darkShadows,
      darkScrollbar,
      darkGraphicCombinations,
      darkTextCombinations,
    );
  }
  return buildTheme(
    'light',
    buildLightColors(lightGraphicCombinations, lightTextCombinations),
    lightShadows,
    lightScrollbar,
    lightGraphicCombinations,
    lightTextCombinations,
  );
};

export type AppTheme = ReturnType<typeof createAppTheme>;

// Default export: light theme (backward compatibility during migration)
const theme = createAppTheme('light');
export default theme;
